// client/src/components/MerchCard.jsx
import React, { useEffect, useMemo, useState } from "react";

const shell = {
  background: "rgba(0, 0, 0, 0.6)",
  border: "1px solid rgba(255, 255, 255, 0.04)",
  borderRadius: 18,
  overflow: "hidden",
  boxShadow: "0 8px 18px rgba(0, 0, 0, 0.65)",
  display: "flex",
  flexDirection: "column",
  gap: 8,
  textDecoration: "none",
  color: "#ecf2ff",
  transition:
    "transform 150ms ease, box-shadow 150ms ease, background 150ms ease",
};

function fmtPrice(p) {
  if (p == null || p === "") return "$0.00";
  const n = Number(p);
  if (!Number.isFinite(n)) return String(p);
  return `$${n.toFixed(2)}`;
}

export default function MerchCard({ product }) {
  const [variants, setVariants] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [price, setPrice] = useState("$0.00");
  const [qty, setQty] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadErr, setLoadErr] = useState("");

  const title = product?.name || "Product";
  const image = product?.thumbnail_url || product?.image || product?.thumb;

  const selectedVariant = useMemo(
    () => variants.find((v) => String(v._id) === String(selectedId)),
    [variants, selectedId]
  );

  function normalizeVariants(raw) {
    const arr =
      (Array.isArray(raw?.variants) && raw.variants) ||
      (Array.isArray(raw?.product?.variants) && raw.product.variants) ||
      (Array.isArray(raw?.result?.variants) && raw.result.variants) ||
      (raw?.result ? [raw.result] : []) ||
      [];

    return arr
      .map((v, i) => {
        const id = v.id ?? v.variant_id ?? v.external_id ?? v.sku ?? `row-${i}`;
        const name =
          v.name ||
          v.size ||
          v.label ||
          [v.product?.name, v.size || v.sku || id].filter(Boolean).join(" / ") ||
          `Variant ${id}`;
        const price = v.retail_price ?? v.price ?? v.price_amount;
        return { _id: String(id), name: String(name), price };
      })
      .filter((v) => v._id);
  }

  useEffect(() => {
    let ignore = false;
    async function load() {
      setLoadErr("");
      try {
        const id = product.id || product.external_id || product.product_id;
        if (!id) throw new Error("missing product id");
        const r = await fetch(`/api/merch/product/${id}`);
        if (!r.ok) throw new Error(`variants HTTP ${r.status}`);
        const j = await r.json();
        const v = normalizeVariants(j);

        if (ignore) return;
        setVariants(v);
        if (v.length) {
          setSelectedId(v[0]._id);
          setPrice(fmtPrice(v[0].price));
        } else {
          setSelectedId("");
          setPrice("$0.00");
          setLoadErr("Unavailable");
        }
      } catch (e) {
        console.warn("[MerchCard] fetch variants failed:", e);
        if (!ignore) {
          setVariants([]);
          setSelectedId("");
          setPrice("$0.00");
          setLoadErr("Unavailable");
        }
      }
    }
    load();
    return () => {
      ignore = true;
    };
  }, [product]);

  useEffect(() => {
    if (selectedVariant) setPrice(fmtPrice(selectedVariant.price));
  }, [selectedVariant]);

  async function handleCheckout(e) {
    e.preventDefault();
    if (!selectedVariant) return;

    try {
      setLoading(true);
      const selectedSyncVariantId = String(selectedVariant._id || "");

      const res = await fetch("/api/merch/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          variantId: selectedSyncVariantId,
          sync_variant_id: selectedSyncVariantId,
          quantity: Number(qty) || 1,
          price: selectedVariant.price,
          name: title,
        }),
      });

      if (!res.ok) {
        let msg = `checkout HTTP ${res.status}`;
        try {
          const data = await res.json();
          if (data?.error) msg = data.error;
        } catch {}
        throw new Error(msg);
      }

      const json = await res.json();
      if (json?.url) {
        window.location.href = json.url;
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (err) {
      console.error("[MerchCard] checkout failed:", err);
      alert(`Checkout failed: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="merch-card" style={shell}>
      <div
        style={{
          width: "100%",
          aspectRatio: "4 / 3",
          background: "rgba(255,255,255,0.04)",
          borderBottom: "1px solid rgba(255,255,255,0.06)",
        }}
      >
        {image ? (
          <img
            src={image}
            alt={title}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
            }}
            loading="lazy"
          />
        ) : (
          <div
            style={{
              width: "100%",
              height: "100%",
              display: "grid",
              placeItems: "center",
              color: "#9ab",
              fontSize: 13,
            }}
          >
            product image
          </div>
        )}
      </div>

      <div
        style={{
          padding: 10,
          display: "grid",
          gap: 8,
        }}
      >
        <div
          style={{
            color: "#dfe8ff",
            fontWeight: 800,
            fontSize: 13,
            lineHeight: 1.3,
          }}
          title={title}
        >
          {title}
        </div>

        <label
          style={{
            color: "#9bb1d1",
            fontSize: 11,
            fontWeight: 700,
            display: "grid",
            gap: 4,
          }}
        >
          <span>Variant / Size</span>
          <select
            value={selectedId}
            onChange={(e) => setSelectedId(e.target.value)}
            disabled={!variants.length}
            style={{
              appearance: "none",
              width: "100%",
              padding: "8px 10px",
              borderRadius: 10,
              background: "rgba(255,255,255,0.03)",
              color: "#eaf1ff",
              border: "1px solid rgba(255,255,255,0.12)",
              outline: "none",
              fontWeight: 700,
              cursor: variants.length ? "pointer" : "not-allowed",
            }}
          >
            {variants.length === 0 ? (
              <option>{loadErr || "Loading…"}</option>
            ) : (
              variants.map((v, i) => (
                <option key={v._id || i} value={String(v._id)}>
                  {v.name} {v.price ? `— ${fmtPrice(v.price)}` : ""}
                </option>
              ))
            )}
          </select>
        </label>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr auto",
            gap: 6,
            alignItems: "center",
          }}
        >
          <button
            type="button"
            onClick={(e) => handleCheckout(e)}
            disabled={!variants.length || loading}
            style={{
              width: "100%",
              padding: "10px 12px",
              borderRadius: 999,
              border: "none",
              cursor: !variants.length || loading ? "not-allowed" : "pointer",
              fontWeight: 800,
              fontSize: 13,
              color: "#fff",
              background:
                "linear-gradient(90deg, #00b7ff 0%, #2f7bff 100%)",
              boxShadow: "0 8px 18px rgba(0, 110, 255, 0.28)",
              opacity: !variants.length || loading ? 0.6 : 1,
            }}
          >
            {loading ? "Redirecting…" : `Select • ${price}`}
          </button>

          <input
            type="number"
            min="1"
            step="1"
            value={qty}
            onChange={(e) => setQty(Math.max(1, Number(e.target.value) || 1))}
            style={{
              width: 58,
              padding: "10px 8px",
              borderRadius: 10,
              background: "rgba(255,255,255,0.03)",
              color: "#eaf1ff",
              border: "1px solid rgba(255,255,255,0.12)",
              outline: "none",
              textAlign: "center",
              fontWeight: 700,
            }}
          />
        </div>
      </div>
    </div>
  );
}